import { Users, MessageSquare, Calendar, TrendingUp, ArrowUp, ArrowDown, Minus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AnimatedNumber } from '@/components/animated-number'
import { FieldHint } from '@/components/field-hint'
import { cn } from '@/lib/utils'
import { useDashboardStats, trendBetween, type DashboardStats, type Trend } from './use-dashboard'
import { useDashboardFilters } from './use-dashboard-filters'
import { previousLabel } from './filters'

type StatCardConfig = {
  readonly key: 'totalLeads' | 'replyRate' | 'meetingsBooked' | 'contentEngagement'
  readonly label: string
  readonly icon: typeof Users
  readonly format: (value: number) => string
  readonly trendSuffix: string
  readonly hint: string
  /** Label and hint when a date range is picked (leads ADDED in it). */
  readonly rangedLabel?: string
  readonly rangedHint?: string
  readonly trendKind: 'percent' | 'points' | 'count'
}

const STAT_CARDS: readonly StatCardConfig[] = [
  {
    key: 'totalLeads',
    label: 'Total Leads',
    icon: Users,
    format: (v) => v.toLocaleString(),
    trendSuffix: '%',
    hint: 'Everyone in your pipeline across all stages and campaigns.',
    rangedLabel: 'Leads Added',
    rangedHint: 'Leads added to your pipeline in the selected dates (and campaigns).',
    trendKind: 'percent',
  },
  {
    key: 'replyRate',
    label: 'Reply Rate',
    icon: MessageSquare,
    format: (v) => `${v}%`,
    trendSuffix: ' pts',
    hint: 'The percent of contacted leads who have replied.',
    rangedHint: 'Of the leads added in the selected dates and contacted so far, the percent who replied.',
    trendKind: 'points',
  },
  {
    key: 'meetingsBooked',
    label: 'Meetings Booked',
    icon: Calendar,
    format: (v) => v.toLocaleString(),
    trendSuffix: '',
    hint: 'How many leads booked a meeting through your outreach.',
    rangedHint: 'Leads added in the selected dates who have booked a meeting so far.',
    trendKind: 'count',
  },
  {
    key: 'contentEngagement',
    label: 'Content Engagement',
    icon: TrendingUp,
    format: (v) => v.toLocaleString(),
    trendSuffix: '%',
    hint: 'Total likes and comments your published posts have earned.',
    trendKind: 'percent',
  },
]

function TrendBadge({ trend, suffix, against }: { readonly trend: Trend; readonly suffix: string; readonly against: string }) {
  if (trend.direction === 'neutral') {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Minus className="h-3 w-3" />
        No change vs {against}
      </p>
    )
  }

  const isUp = trend.direction === 'up'
  const Icon = isUp ? ArrowUp : ArrowDown

  return (
    <p
      className={cn(
        'text-xs flex items-center gap-1',
        isUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-300 dark:text-red-400',
      )}
    >
      <Icon className="h-3 w-3" />
      {trend.value}{suffix} vs {against}
    </p>
  )
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-4 rounded" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-7 w-16 mb-1" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  )
}

const STAT_KEYS = ['totalLeads', 'replyRate', 'meetingsBooked', 'contentEngagement'] as const

function trendsAgainst(current: DashboardStats, previous: DashboardStats): DashboardStats['trends'] {
  const kinds = Object.fromEntries(STAT_CARDS.map((c) => [c.key, c.trendKind]))
  return Object.fromEntries(
    STAT_KEYS.map((k) => [k, trendBetween(current[k], previous[k], kinds[k] ?? 'percent')]),
  ) as DashboardStats['trends']
}

export function StatsCards() {
  const { query, previousQuery, range } = useDashboardFilters()
  const { data: stats, isLoading, isPlaceholderData } = useDashboardStats(query)
  // A bounded range compares with the same length of time just before it.
  const { data: previous, isPlaceholderData: previousIsPlaceholder } = useDashboardStats(previousQuery)
  const ranged = range.from !== null || range.to !== null
  const comparable = previousQuery !== null && stats && previous && !isPlaceholderData && !previousIsPlaceholder
  const trends = comparable ? trendsAgainst(stats, previous) : null
  const against = previousLabel(range)

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {STAT_CARDS.map((config) => {
        const Icon = config.icon
        const value = stats?.[config.key] ?? 0
        const trend = trends?.[config.key]
        const label = ranged && config.rangedLabel ? config.rangedLabel : config.label
        const hint = ranged && config.rangedHint ? config.rangedHint : config.hint

        return (
          <Card key={config.key} className="group relative overflow-hidden">
            {/* Subtle cobalt glow corner — only visible on hover, reinforces the brand feel */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[var(--color-cobalt)]/0 blur-2xl transition-all duration-300 group-hover:bg-[var(--color-cobalt)]/15"
            />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-1">
                <CardTitle className="eyebrow text-[var(--color-silver)]">{label}</CardTitle>
                <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>
              </div>
              <Icon className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
            </CardHeader>
            <CardContent>
              {/*
                Number entrance — drops down from above on first mount, then
                tweens through intermediate values whenever the prop
                changes. The overflow-clip container keeps the drop-in
                animation from bleeding past the card edge.
                */}
              <div className="overflow-clip-y text-3xl font-bold tracking-tight text-[var(--color-silver-bright)]">
                <AnimatedNumber
                value={value}
                duration={1100}
                format={(n) => config.format(Math.round(n))}
                className="inline-block animate-digit-drop"
              />
              </div>
              {/* Content engagement is not tracked per period yet, so it never shows a trend. */}
              {trend && config.key !== 'contentEngagement' && (
                <TrendBadge trend={trend} suffix={config.trendSuffix} against={against} />
              )}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
